import { NestedKey } from '@40seas/entities/common';
import { any, array, boolean, date, number, object, string, z } from 'zod';
import 'zod-openapi/extend';

export type FieldFilter<T> = Partial<{ [key in NestedKey<T>]: z.infer<typeof FieldFilterSchema> }>;

export type AggregateOperator<T> = {
    or?: FieldFilter<T>[];
    and?: QueryFilter<T>[];
};

export type QueryFilter<T> = FieldFilter<T> & AggregateOperator<T>;

export type QuerySort<T> = { [field in keyof Partial<T>]: 1 | -1 };

export class QueryDto<T> {
    filter?: QueryFilter<T>;
    sort?: QuerySort<T>;
    limit?: number;
    page?: number;
    expand?: NestedKey<T>[];
    distinctBy?: NestedKey<T>;
    aggregate?: {
        groupBy?: NestedKey<T>[];
        sum?: NestedKey<T>;
        count?: boolean;
    };
}

export const QueryDtoSchema = (entity: z.AnyZodObject, entities: Array<new () => any>) =>
    object({
        filter: getQueryFilter(entity, entities).optional(),
        sort: getSortField(entity).optional(),
        limit: z.coerce.number().optional().openapi({ type: 'number' }),
        page: z.coerce.number().optional().openapi({ type: 'number' }),
        expand: array(string()).optional(),
        distinctBy: string().optional(),
        aggregate: getAggregateSchema(entity),
    }).strict();

export const QueryOperatorSchema = object({
    eq: z.coerce.number().or(string()).or(boolean()).optional(),
    ne: z.coerce.number().or(string()).or(boolean()).or(array(string())).optional(),
    in: array(string()).optional(),
    nin: array(string()).optional(),
    regex: any().optional(),
    options: any().optional(),
    gte: z.coerce.number().or(string()).or(date()).optional(),
    gt: z.coerce.number().or(string()).or(date()).optional(),
    lte: z.coerce.number().or(string()).or(date()).optional(),
    lt: z.coerce.number().or(string()).or(date()).optional(),
    size: z.coerce.number().optional(),
    exists: boolean().optional(),
    elemMatch: object({}).optional(),
})
    .strict()
    .refine((obj: Record<string | number | symbol, unknown>) => Object.values(obj).some((v) => v !== undefined));

const FieldFilterSchema = z.union([QueryOperatorSchema, z.string(), z.number(), z.boolean()]).openapi({ ref: 'FieldFilter' });

const getFieldFilterSchema = (keys: string[], entity?: z.AnyZodObject) => {
    const validKeys = entity ? filterOutComputedFields(keys, entity) : keys;

    return z.object(
        validKeys.reduce((schema: Record<string, z.ZodTypeAny>, field) => {
            if (field === 'metadata') {
                schema[field] = z.record(z.string(), FieldFilterSchema);
            } else {
                schema[field] = FieldFilterSchema;
            }
            return schema;
        }, {}),
    );
};

const getAggregateOperatorSchema = (zodKeys: string[], entity?: z.AnyZodObject) =>
    object({
        or: z.array(getFieldFilterSchema(zodKeys, entity).partial().strict()).optional(),
        and: z.array(getFieldFilterSchema(zodKeys, entity).partial().strict()).optional(),
    });

const getQueryFilter = (entity: z.AnyZodObject, entities: Array<new () => any>) => {
    const mainEntityKeys = zodKeys(entity);

    // Generate additional entity keys with prefixes for nested properties (e.g., 'buyer.displayName' in invoice)
    // Note: Filtering on these nested properties requires that the user explicitly expands the related entity
    const additionalEntityKeys = entities.flatMap((EntityClass) => {
        const entityName = EntityClass.name.toLowerCase();

        return zodKeys(EntityClass['schema']).map((key) => `${entityName}.${key}`);
    });

    const allKeys = [...mainEntityKeys, ...additionalEntityKeys];

    // Pass entity schema to filter out computed fields (only main entity fields are checked,
    // as additional entity keys like 'buyer.displayName' are from expanded entities)
    return getFieldFilterSchema(allKeys, entity).merge(getAggregateOperatorSchema(allKeys, entity)).strict().partial();
};

const getSortField = (entity: z.AnyZodObject) => {
    const validKeys = filterOutComputedFields(zodKeys(entity), entity);

    return object(
        validKeys.reduce((prev: any, field) => {
            prev[field] = z.union([z.literal('1'), z.literal('-1')]);
            return prev;
        }, {}),
    )
        .partial()
        .strict();
};

// Checks if a Zod schema field is marked as computed via x-computed OpenAPI extension
const isComputedField = (schema: z.ZodTypeAny): boolean => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const def = (schema as any)._def;
    // Check for openapi metadata first (before unwrapping)
    if (def?.openapi?.['x-computed'] === true) return true;
    // Handle wrapped types (optional, nullable)
    if (def?.innerType) return isComputedField(def.innerType);
    // Handle ZodEffects (from z.preprocess, etc.)
    if (def?.schema) return isComputedField(def.schema);
    return false;
};

// Filters out computed fields from the list of valid keys.
// Computed fields (marked with x-computed: true) are calculated at runtime
// and not stored in the database, so they cannot be used for filtering, sorting, or aggregation.
// NOTE: Only root-level computed fields are detected (e.g., 'availableCredit').
// Nested computed fields (e.g., 'result.computedField') are not detected.
// Keys not found in entity.shape (e.g., additional entity keys like 'buyer.displayName') are preserved.
const filterOutComputedFields = (keys: string[], entity: z.AnyZodObject): string[] =>
    keys.filter((fieldName) => {
        const rootField = fieldName.split('.')[0];
        const fieldSchema = entity.shape[rootField];
        // Keep key if not found in schema (additional entity keys) or if not computed
        return !fieldSchema || !isComputedField(fieldSchema);
    });

// Note: Unlike getQueryFilter, aggregate validation only uses main entity keys (not expanded entity keys).
// This is intentional because aggregation runs BEFORE expansion (populate) in MongoDB, so expanded
// fields like 'buyer.displayName' don't exist at aggregation time. You can only aggregate on:
// - Direct fields: 'amount', 'status'
// - Nested object properties: 'result.paymentOption.days'
const getAggregateSchema = (entity: z.AnyZodObject) => {
    const validKeys = filterOutComputedFields(zodKeys(entity), entity);
    const validateField = (field: string) => validKeys.includes(field);

    return object({
        groupBy: array(
            string().refine(validateField, (field) => ({
                message: `Invalid groupBy field: '${field}'`,
            })),
        )
            .min(1)
            .optional()
            .openapi({
                description: 'Fields to group results by. Only stored fields can be used for grouping.',
            }),
        sum: string()
            .refine(validateField, (field) => ({
                message: `Invalid sum field: '${field}'`,
            }))
            .optional()
            .openapi({
                description: 'Field to sum. Only numeric stored fields can be used for aggregation.',
            }),
        count: boolean().optional().openapi({
            description: 'Include count of records in each group.',
        }),
    })
        .optional()
        .openapi({
            description: 'Aggregation options for grouping and summing results.',
        });
};

// taken from https://github.com/colinhacks/zod/discussions/2134#discussioncomment-5194111
// TBD - does not support refines or effects, so we could query only for refines root object
const zodKeys = <T extends z.ZodTypeAny>(schema: T): string[] => {
    // make sure schema is not null or undefined
    if (schema === null || schema === undefined) return [];
    // check if schema is nullable or optional
    if (schema instanceof z.ZodNullable || schema instanceof z.ZodOptional) return zodKeys(schema.unwrap());
    // check if schema is an array
    if (schema instanceof z.ZodArray) return zodKeys(schema.element);
    // check if schema is an object
    if (schema instanceof z.ZodObject) {
        // get key/value pairs from schema
        const entries = Object.entries(schema.shape);
        // loop through key/value pairs
        return entries.flatMap(([key, value]) => {
            // get nested keys
            const nested = value instanceof z.ZodType ? zodKeys(value).map((subKey) => `${key}.${subKey}`) : [];

            // if value is an effect, union or intersection, support nested keys with a wildcard
            // can lead to false positives, but it's better than true negatives for querying

            // currently we dont use the wildcard, we need to use a zod record and not an object
            // to use it as we want to apply a regex on the key.

            // if (value instanceof z.ZodEffects || value instanceof z.ZodUnion || value instanceof z.ZodIntersection) {
            //     nested.push(`${key}.*`);
            // }

            // return nested keys
            return nested.length ? nested : key;
        });
    }
    // return empty array
    return [];
};
